// app/frontend/data/artistStarData.tsx
import React from 'react';
import { Anchor, Text } from '@mantine/core';

export interface FAQItem {
  question: string;
  answer: string | React.ReactNode;
}

export interface FAQSection {
  section: string;
  items: FAQItem[];
}

export const artistStarData: FAQSection[] = [
  {
    section: 'Why Buy Artist Stars?',
    items: [
      {
        question: 'Why Buy Artist Stars?',
        answer: (
          <ul>
            <li className="mb-3">
              <strong>Support our dance artists!</strong> Our dance artists around the world put in so much work to keep
              our dance scene alive and to keep the languages of our dance styles alive! Artist stars are a way for them
              to earn directly from supporters like you!
            </li>
            <li className="mb-3">
              <strong>Support WCWD!</strong> Our site has had the mission to bring all of your dance events in one
              freaking place—from local city, artist, organizer, and global dance style calendars for every dance style
              we can find!
            </li>
          </ul>
        ),
      },
    ],
  },
  {
    section: 'Steps to Set Up Your Artist Profile To Receive Stars',
    items: [
      {
        question: 'How do I set up my artist profile to receive stars?',
        answer: (
          <ul>
            <li className="mb-3">
              <strong>Step 1:</strong>{' '}
              <Anchor href="https://wherecanwedance.com/register" target="_blank" c="orange" fw={700} mb="1rem">
                Create a user account
              </Anchor>{' '}
              on WCWD or{' '}
              <Anchor href="https://wherecanwedance.com/login" target="_blank" c="orange" fw={700}>
                log in
              </Anchor>{' '}
              to your existing account.
            </li>
            <li className="mb-3">
              <strong>Step 2:</strong> Create an{' '}
              <Anchor href="https://wherecanwedance.com/suggest_artist" target="_blank" c="orange" fw={700}>
                artist profile
              </Anchor>{' '}
              on WCWD if you haven't already.
            </li>
            <li className="mb-3">
              <strong>Step 3:</strong>{' '}
              <Anchor href="/login" target="_blank " c="orange" fw={700}>
                Claim your artist profile
              </Anchor>{' '}
              on WCWD if you haven't already.
            </li>
            <li className="mb-3">
              <strong>Step 4:</strong> Connect your Stripe account to your artist profile.
            </li>
            <li>
              <strong>Step 5:</strong> Once Stripe is successfully connected, you can start receiving stars from
              supporters through the support button on your artist profile page!
            </li>
          </ul>
        ),
      },
    ],
  },
  {
    section: 'Supporters',
    items: [
      {
        question: 'What are artist stars?',
        answer:
          'Artist stars represent online donations that supporters can purchase for artists they want to support and show appreciation toward.',
      },
      {
        question: 'How much does a star cost?',
        answer:
          'Each artist star is $5 USD and can be bought in multiples. For example, 10 artist stars = $50 USD, 100 artist stars = $500 USD.',
      },
      {
        question: 'Does the money go straight to the artist?',
        answer:
          'Yes, artists have their Stripe accounts connected to their bank accounts, and the money from artist stars is directly deposited by Stripe.',
      },
      {
        question: 'Can I use stars to pay for private lessons & other services?',
        answer:
          'Yes, 100%! For example, if a private lesson costs $100 USD, you can purchase 20 stars (20 × $5.00 USD = $100 USD) for the artist.',
      },
      {
        question: 'Can I get a refund?',
        answer: 'Refunds are possible through Stripe as long as the purchase was made within 90 days.',
      },
      {
        question: 'Can I pay with Venmo, Zelle, or Paypal?',
        answer: 'Currently we only accept payments through Stripe.',
      },
    ],
  },
  {
    section: 'Artists',
    items: [
      {
        question: 'What is Stripe?',
        answer:
          'Stripe is a global payment processing platform many websites use to securely process payments around the world.',
      },
      {
        question: 'How do I create a Stripe account?',
        answer:
          'Under Settings > Payments, after claiming your artist profile you’ll see a button to connect to Stripe, which will walk you through the setup.',
      },
      {
        question: 'Is there a maximum amount I can make?',
        answer: 'No, there is no maximum on what you can earn.',
      },
      {
        question: 'Is there a maximum amount per transaction?',
        answer: 'Yes, the maximum amount per transaction is $1,000 USD.',
      },
      {
        question: 'Do supporters need a WCWD account to purchase stars?',
        answer:
          'No, they can simply add a display name, email address, and purchase as a guest. No account is required.',
      },
      {
        question: 'How much does this cost to set up and use?',
        answer: (
          <ul>
            <li className="mb-3">There are no monthly or setup fees. We only make money if you do.</li>
            <li className="mb-3">
              We charge a 5% platform fee on top of the star amount, plus Stripe’s processing fees. For example, if
              someone buys 1 star for $5:
              <ul className="mt-2">
                <li className="mb-2">They are charged $5.70 USD</li>
                <li className="mb-2">You receive $5 USD</li>
                <li className="mb-2">The site earns $0.25 USD</li>
              </ul>
            </li>
          </ul>
        ),
      },
      {
        question: 'Can I transfer my stars to another artist?',
        answer: 'Currently we do not offer a feature to transfer stars to another artist.',
      },
      {
        question: 'Can I receive my stars in Venmo, Zelle, Wise, or Paypal instead of Stripe?',
        answer: 'Currently we only support payouts via Stripe.',
      },
    ],
  },
];
